import * as moment from 'moment-timezone'
import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
import FieldQuoteFormula from '@/components/FieldQuoteFormula'
import { getCountries } from '@/services/country'

const quoteProvidersModel = vm => ({
  title: vm.$gettext('Provider Quotes'),
  endpoint: 'quote-providers',
  allowNew: true,
  allowDelete: true,
  allowBulk: true,
  countrize: 'country_iso_code',
  fields: [
    {
      key: 'id',
      sortable: true
    },
    {
      key: 'reference',
      label: vm.$gettext('Quote Name'),
      required: true,
      sortable: true,
      editable: true
    },
    {
      key: 'country_iso_code',
      label: vm.$gettext('Country'),
      sortable: true,
      editable: true,
      required: true,
      description: vm.$gettext('ISO Code pointing to countries, to which country this Quote applies for'),
      type: 'select',
      options: 'countries',
      showKey: 'iso_code',
      showField: 'name',
      withSelect: true,
      default: null,
      disabledCondition: item => {
        return !!item.id
      }
    },
    {
      key: 'company_id',
      label: vm.$gettext('Provider'),
      editable: true,
      required: true,
      componentEdit: (value, key, item) => {
        return {
          is: CompanyAutocomplete,
          props: {
            required: true,
            restrictTo: [['relationship', 'eq', 'provider']],
            preQuery: item.company ? item.company.name : null
          },
          on: {
            selected: result => {
              if (result) {
                item[key] = result.id
              } else {
                item[key] = null
              }
            }
          }
        }
      },
      formatter: (value, key, item) => {
        if (item.company) {
          const html = `<strong class="badge" style="background-color: ${vm.stringToColour(
            ' ' + item.company.id + item.company.id + item.company.id + item.company.id + item.company.id
          )};${vm.textContrastColor(
            vm.stringToColour(
              ' ' + item.company.id + item.company.id + item.company.id + item.company.id + item.company.id
            )
          )}">${item.company.name}</strong>`
          return html
        }
      }
    },
    {
      key: 'area_id_from',
      label: vm.$gettext('Origin Area'),
      type: 'select',
      options: 'areas',
      showKey: 'id',
      showField: 'name',
      withSelect: true,
      editable: true,
      sortable: true,
      required: false,
      description: vm.$gettext('Origin Area for this Quote'),
      default: null,
      disabledCondition: item => {
        return !!(item.id || item.region_id_from || item.region_id_to)
      }
    },
    {
      key: 'area_id_to',
      label: vm.$gettext('Destination Area'),
      type: 'select',
      options: 'areas',
      showKey: 'id',
      showField: 'name',
      withSelect: true,
      editable: true,
      sortable: true,
      required: false,
      description: vm.$gettext('Destination Area for this Quote'),
      default: null,
      disabledCondition: item => {
        return !!(item.id || item.region_id_from || item.region_id_to)
      }
    },
    {
      key: 'region_id_from',
      label: vm.$gettext('Origin Region'),
      type: 'select',
      options: 'regions',
      showKey: 'id',
      showField: 'name',
      withSelect: true,
      editable: true,
      sortable: true,
      required: false,
      description: vm.$gettext('Origin Region this quote is for'),
      default: null,
      disabledCondition: item => {
        return !!(item.id || item.area_id_from || item.area_id_to)
      }
    },
    {
      key: 'region_id_to',
      label: vm.$gettext('Destination Region'),
      type: 'select',
      options: 'regions',
      showKey: 'id',
      showField: 'name',
      withSelect: true,
      editable: true,
      sortable: true,
      required: false,
      description: vm.$gettext('Destination Region this quote is for'),
      default: null,
      disabledCondition: item => {
        return !!(item.id || item.area_id_from || item.area_id_to)
      }
    },
    {
      key: 'size_id',
      label: vm.$gettext('Size'),
      type: 'select',
      options: 'shippingSizes',
      showKey: 'id',
      showField: 'name',
      withSelect: true,
      editable: true,
      sortable: false,
      required: false,
      description: vm.$gettext('Shipping size this quote is for'),
      default: null
    },
    {
      key: 'shipping_type_id',
      label: vm.$gettext('Shipping Type'),
      type: 'select',
      options: 'shippingTypes',
      showKey: 'id',
      showField: 'name',
      withSelect: true,
      editable: true,
      sortable: true,
      required: false,
      description: vm.$gettext('Shipping type this quote is for'),
      default: null
    },
    {
      key: 'formula',
      label: vm.$gettext('Formula'),
      editable: true,
      required: true,
      description: vm.$gettext("Must be a number (variables won't work)"),
      type: 'number',
      step: '0.01',
      componentEdit: (value, key, item) => {
        return {
          is: FieldQuoteFormula,
          props: {
            required: true,
            quote: item,
            variables: ['{kgms}'],
            fieldName: 'formula',
            customValuesField: 'formula_params',
            excludedVariables: ['kgms']
          }
        }
      }
    },
    {
      key: 'formula_params',
      editable: true,
      hidden: true
    },
    {
      key: 'date_from',
      label: vm.$gettext('From Date'),
      sortable: true,
      editable: true,
      type: 'datetime',
      formatter: (value, key, item) => {
        if (value) {
          const tz = getCountries().find(x => x.iso_code === item.country_iso_code).front_config.i18n.tz
          return moment.utc(value).tz(tz).format('L LT')
        }
      },
      description: vm.$gettext('Date since the Quote applies'),
      default: null
    },
    {
      key: 'date_to',
      label: vm.$gettext('To Date'),
      sortable: true,
      editable: true,
      type: 'datetime',
      formatter: (value, key, item) => {
        if (value) {
          const tz = getCountries().find(x => x.iso_code === item.country_iso_code).front_config.i18n.tz
          return moment.utc(value).tz(tz).format('L LT')
        }
      },
      description: vm.$gettext('Date until the Quote applies'),
      default: null
    }
  ],
  filters: [
    'id',
    'reference',
    'company.name',
    'country_iso_code',
    'area_id_from',
    'area_id_to',
    'region_id_from',
    'region_id_to',
    'shipping_type.name'
  ],
  includes: ['company', 'area_from', 'area_to'],
  exclude: ['formula_params']
})

export default quoteProvidersModel
